import React, { useEffect, useRef, useState, useMemo } from "react";
import { DrawingManager, Polygon, useGoogleMap } from "@react-google-maps/api";
import { Icon } from "@iconify/react";

/* ================= HELPERS ================= */
const normalizePolygons = (data) => {
  if (!data) return [];
  let parsed;
  try { 
    parsed = typeof data === "string" ? JSON.parse(data) : data; 
  } catch { 
    return []; 
  }
  
  // If it's the structure: [{path: [...]}, {path: [...]}]
  if (Array.isArray(parsed) && parsed.length && parsed[0].path) {
    return parsed.map(p => p.path);
  }
  // If it's already an array of arrays: [[{lat,lng}]]
  if (Array.isArray(parsed) && parsed.every(p => Array.isArray(p))) {
    return parsed.filter(p => p.length);
  }
  // If it's a single array: [{lat,lng}]
  if (Array.isArray(parsed) && parsed.length && parsed[0].lat !== undefined) {
    return [parsed];
  }
  return [];
};

const polygonToPath = (polygon) =>
  polygon.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));

const THEME_COLORS = {
  primary: "#0d6efd",   
  selected: "#ef4444",  
  fillOpacity: 0.2,
  strokeWeight: 3
};

const DrawTools = ({ data, onChange }) => {
  const map = useGoogleMap();
  const [polygons, setPolygons] = useState([]);
  const [selectedPathIndex, setSelectedPathIndex] = useState(null);
  const [isLibLoaded, setIsLibLoaded] = useState(false);
  
  const drawingManagerRef = useRef(null);
  const dataLoadedRef = useRef(false);

  // 1. Library Detection
  useEffect(() => {
    const checkDrawingLib = setInterval(() => {
      if (window.google?.maps?.drawing) {
        setIsLibLoaded(true);
        clearInterval(checkDrawingLib);
      }
    }, 100);
    return () => clearInterval(checkDrawingLib);
  }, []);

  // 2. Initial Data Load (Sync from Form)
  useEffect(() => {
    if (data && !dataLoadedRef.current) {
      const normalized = normalizePolygons(data);
      setPolygons(normalized);
      dataLoadedRef.current = true;
    }
  }, [data]);

  const syncChanges = (updatedList) => {
    setPolygons(updatedList);
    // Important: We send the stringified version back to the form
    onChange(JSON.stringify(updatedList));
  };

  const onPolygonComplete = (polygon) => {
    const newPath = polygonToPath(polygon);
    polygon.setMap(null); 
    syncChanges([...polygons, newPath]);
    
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
    }
  };

  const drawingManagerOptions = useMemo(() => ({
    drawingControl: true,
    drawingControlOptions: {
      position: window.google?.maps?.ControlPosition?.TOP_CENTER,
      drawingModes: ["polygon"]
    },
    polygonOptions: {
      fillColor: THEME_COLORS.primary,
      fillOpacity: THEME_COLORS.fillOpacity,
      strokeColor: THEME_COLORS.primary,
      strokeWeight: THEME_COLORS.strokeWeight,
      editable: true,
      zIndex: 1
    }
  }), []);

  if (!isLibLoaded || !map) return null;

  return (
    <>
      {/* TOOLBAR */}
      <div className="position-absolute top-0 start-50 d-flex gap-2" 
           style={{ zIndex: 1000, marginTop: '10px', transform: 'translateX(-50%)' }}>
        {selectedPathIndex !== null && (
          <button 
            type="button" 
            className="btn bg-danger text-white radius-8 border-0 shadow-sm d-flex align-items-center gap-2 px-12 py-6"
            onClick={() => {
              const updated = polygons.filter((_, i) => i !== selectedPathIndex);
              setSelectedPathIndex(null);
              syncChanges(updated);
            }}
          >
            <Icon icon="lucide:trash-2" /> Delete Selected
          </button>
        )}
      </div>

      <DrawingManager
        onLoad={(dm) => { drawingManagerRef.current = dm; }}
        onPolygonComplete={onPolygonComplete}
        options={drawingManagerOptions}
      />

      {polygons.map((path, index) => (
        <Polygon
          key={`poly-${index}-${path.length}`} // Key helps React identify path changes
          paths={path}
          onLoad={(poly) => {
            const updatePath = () => {
              const newPath = polygonToPath(poly);
              setPolygons(prev => {
                const next = [...prev];
                next[index] = newPath;
                // Defer onChange to prevent state collision during drag
                setTimeout(() => onChange(JSON.stringify(next)), 0);
                return next;
              });
            };
            window.google.maps.event.addListener(poly.getPath(), "set_at", updatePath);
            window.google.maps.event.addListener(poly.getPath(), "insert_at", updatePath);
          }}
          onClick={() => setSelectedPathIndex(index)}
          options={{
            fillColor: selectedPathIndex === index ? THEME_COLORS.selected : THEME_COLORS.primary,
            fillOpacity: THEME_COLORS.fillOpacity,
            strokeColor: selectedPathIndex === index ? THEME_COLORS.selected : THEME_COLORS.primary,
            strokeWeight: THEME_COLORS.strokeWeight,
            editable: selectedPathIndex === index,
            draggable: selectedPathIndex === index
          }}
        />
      ))}
    </>
  );
};

export default DrawTools;